import React from 'react';
import {
  XMarkIcon,
  PencilSquareIcon,
  EyeIcon,
  EyeSlashIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BellIcon,
  GlobeAltIcon,
  CalendarIcon,
  DocumentTextIcon,
  CubeIcon,
  PaintBrushIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  StarIcon,
  UserCircleIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { modulesForVisibilityUi, type ModuleDef } from '../../hooks/useModuleVisibility';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard: ChartBarIcon,
  Bell: BellIcon,
  Globe: GlobeAltIcon,
  Calendar: CalendarIcon,
  FileText: DocumentTextIcon,
  Banknotes: BanknotesIcon,
  Package: CubeIcon,
  PaintBrush: PaintBrushIcon,
  ShoppingCart: ShoppingCartIcon,
  Users: UserGroupIcon,
  Star: StarIcon,
  BarChart2: ChartBarIcon,
  Settings: Cog6ToothIcon,
  UserCog: UserCircleIcon,
  MessageCircle: ChatBubbleLeftRightIcon,
  CreditCard: CreditCardIcon,
};

const GROUP_LABELS: Record<string, string> = {
  core: 'Principal',
  ventas: 'Ventas',
  operaciones: 'Operaciones',
  analytics: 'Analytics / BI',
  config: 'Configuración',
};

function ModuleRow({
  module,
  visible,
  onToggle,
}: {
  module: ModuleDef;
  visible: boolean;
  onToggle: (id: string) => void;
}) {
  const Icon = ICON_MAP[module.icon] ?? CubeIcon;

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
        visible
          ? 'bg-white border border-gray-100 shadow-sm'
          : 'bg-gray-50 border border-dashed border-gray-200 opacity-70'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            visible ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-400'
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <span className={`text-sm font-medium ${visible ? 'text-gray-800' : 'text-gray-400'}`}>
          {module.label}
        </span>
      </div>
      <button
        type="button"
        onClick={() => onToggle(module.id)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
          visible
            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
            : 'bg-white text-gray-500 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
        }`}
      >
        {visible ? (
          <>
            <EyeIcon className="w-3 h-3" />
            Visible
          </>
        ) : (
          <>
            <EyeSlashIcon className="w-3 h-3" />
            Oculto
          </>
        )}
      </button>
    </div>
  );
}

export function ModuleManagerPanel({
  isOpen,
  onClose,
  visibilityMap,
  onToggle,
}: {
  isOpen: boolean;
  onClose: () => void;
  visibilityMap: Record<string, boolean>;
  onToggle: (id: string) => void | Promise<void>;
}) {
  const uiModules = modulesForVisibilityUi();
  const visibleCount = uiModules.filter((m) => visibilityMap[m.id] !== false).length;

  if (!isOpen) return null;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          role="button"
          tabIndex={0}
          aria-label="Cerrar panel"
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-base font-bold text-gray-900">Editar módulos</h2>
              <p className="text-xs text-gray-500">
                Mostrar u ocultar secciones del menú. {visibleCount} de {uiModules.length} visibles.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
            aria-label="Cerrar"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {Object.entries(GROUP_LABELS).map(([group, label]) => {
            const modules = uiModules.filter((m) => m.group === group);
            if (!modules.length) return null;
            return (
              <div key={group}>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                  {label}
                </p>
                <div className="space-y-2">
                  {modules.map((m) => (
                    <ModuleRow
                      key={m.id}
                      module={m}
                      visible={visibilityMap[m.id] !== false}
                      onToggle={onToggle}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Usa Visible/Oculto para mostrar u ocultar cada sección en el menú. Los cambios se guardan solos.
          </p>
        </div>
      </div>
    </>
  );
}

/** Button to open the panel where users show/hide sidebar modules. */
export function AddModuleButton({
  hiddenCount,
  onClick,
}: {
  hiddenCount: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-dashed text-indigo-600 hover:bg-indigo-50 border-indigo-200"
      title="Mostrar u ocultar secciones del menú (ej. Ventas, Clientes)"
    >
      <div className="w-5 h-5 rounded-full flex items-center justify-center bg-indigo-100">
        <PencilSquareIcon className="w-3 h-3 text-indigo-600" />
      </div>
      <span>Editar módulos</span>
      {hiddenCount > 0 && (
        <span className="ml-auto bg-indigo-600 text-white text-xs rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center">
          {hiddenCount} ocultos
        </span>
      )}
    </button>
  );
}
